import React from 'react';
import {makeStyles} from 'tss-react/mui';
import {keyframes} from '@mui/system';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandedIcon from '@mui/icons-material/ChevronRight';
import {ObjectStrKeys, PtrnDataInput, PtrnState} from '../../patronTypes';
import {GetRowIdFn} from '../PtrnTablaBase/PtrnTablaBaseTypes';

//#region estilos
// Define keyframes using @mui/system
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

export const useTablaArbolStyles = makeStyles()((theme) => ({
   /* Animation for expanding rows */
   rowExpand: {
      animation: `${fadeIn} 0.3s ease-in-out forwards`
   },

   /* Animation for collapsing rows */
   rowCollapse: {
      animation: `${fadeOut} 0.3s ease-in-out forwards`
   }
}));
//#endregion

//#region componentes
export function IconButtonExpand({
   isExpanded,
   onClick
}: {
   isExpanded: boolean;
   onClick: () => void;
}) {
   return (
      <IconButton size="small" onClick={onClick}>
         {isExpanded ? <KeyboardArrowDownIcon /> : <ExpandedIcon />}
      </IconButton>
   );
}
export function IconButtonInvisible() {
   return (
      <IconButton disabled size="small" style={{color: 'transparent'}}>
         <KeyboardArrowDownIcon />
      </IconButton>
   );
}
//#endregion

//#region manejo arbol
export function createNodeDepthChildrenDictionaries(
   tree: PtrnDataInput[],
   getNodeId: GetRowIdFn,
   getPadreId: (rowData: PtrnDataInput) => string | null
): {parentsDepth: Record<string, number>; parentsChildren: Record<string, string[]>} {
   // agrupacion de nodos por padre
   const parentMap = tree.reduce<Record<string, string[]>>((map, node) => {
      const parentId = getPadreId(node) ?? 'null'; // Treat `null` as a string key

      if (!map[parentId]) {
         map[parentId] = [];
      }
      map[parentId].push(getNodeId(node));
      return map;
   }, {});

   // calcula recursivamente la profundidad de un nodo padre
   const calculateParentsDepths = (parentId: string, depth: number): Record<string, number> => {
      let result: Record<string, number> = {};
      const childrenIds = parentMap[parentId] || [];

      if (childrenIds.length > 0) {
         // Add the current parent node and its depth if it has children
         for (const childId of childrenIds) {
            if (Boolean(parentMap[childId])) {
               result = {
                  ...result,
                  [childId]: depth + 1,
                  ...calculateParentsDepths(childId, depth + 1)
               };
            }
         }
      }
      return result;
   };

   // logica principal
   const rootNodes = parentMap['null'] || [];

   // no existen raices
   if (rootNodes.length === 0) {
      return {parentsDepth: {}, parentsChildren: {}};
   }

   // multiples raizes, calcular la profundidad para todas las raices y sus respectivos hijos
   return {
      parentsDepth: rootNodes.reduce((result, root) => {
         return {...result, [root]: 0, ...calculateParentsDepths(root, 0)};
      }, {}),
      parentsChildren: parentMap
   };
}
//#endregion
